import React, { useEffect, useRef } from "react";
import {
  X,
  Mail,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  Briefcase,
  Edit,
  Trash2,
  User,
} from "lucide-react";

const EmployeeModal = ({ employee, onClose, onEdit, onDelete }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [onClose]);

  if (!employee) return null;

  const InfoItem = ({ icon: Icon, label, value, iconColor = "text-primary" }) => (
    <div className="flex items-center gap-3 py-2">
      <div className={`p-1.5 rounded-lg bg-gray-100 ${iconColor}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {label}
        </p>
        <p className="text-sm text-gray-900 font-medium break-words">
          {value}
        </p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden mx-4 transform transition-all duration-300 scale-100"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-primary to-primary-dark px-6 py-4 text-white">
          <button
            onClick={onClose}
            className="absolute top-3 right-4 p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-full">
              <User className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Employee ID Card</h2>
              <p className="text-blue-100 mt-0.5 text-sm">Employee Information Details</p>
            </div>
          </div>
        </div>

        {/* ID Card Style Content */}
        <div className="p-6">
          <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              
              {/* Left side - Photo Only */}
              <div className="w-full lg:w-1/3 p-6 flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10 border-r border-gray-200">
                <div>
                  {employee.photo ? (
                    <div className="w-40 h-48 lg:w-48 lg:h-56 rounded-2xl overflow-hidden shadow-lg bg-gray-100 border-4 border-white">
                      <img
                        src={`${process.env.REACT_APP_API_URL}${employee.photo}`}
                        alt={employee.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/192x224?text=No+Image";
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-40 h-48 lg:w-48 lg:h-56 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center shadow-lg border-4 border-white">
                      <div className="text-center">
                        <User className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <span className="text-gray-500 font-medium text-sm">No Photo</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right side - All Information */}
              <div className="flex-1 p-6">
                <div className="space-y-6">
                  
                  {/* Name and Role Section */}
                  <div className="border-b border-gray-200 pb-4">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {employee.name}
                    </h3>
                    <div className="flex flex-wrap gap-3 items-center">
                      <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-lg font-semibold">
                        <Briefcase className="w-4 h-4" />
                        {employee.role}
                      </div>
                      <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium">
                        <User className="w-4 h-4" />
                        ID: EMP-{employee.id?.toString().padStart(4, '0') || '0000'}
                      </div>
                    </div>
                  </div>

                  {/* All Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide border-b border-gray-300 pb-1">
                        Contact Information
                      </h4>
                      
                      <InfoItem
                        icon={Mail}
                        label="Email Address"
                        value={employee.email}
                        iconColor="text-blue-500"
                      />
                      
                      <InfoItem
                        icon={Phone}
                        label="Phone Number"
                        value={employee.phone}
                        iconColor="text-green-500"
                      />
                      
                      <InfoItem
                        icon={MapPin}
                        label="Address"
                        value={employee.address}
                        iconColor="text-red-500"
                      />
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide border-b border-gray-300 pb-1">
                        Personal Details
                      </h4>
                      
                      <InfoItem
                        icon={Calendar}
                        label="Age"
                        value={`${employee.age} years old`}
                        iconColor="text-purple-500"
                      />
                      
                      <InfoItem
                        icon={DollarSign}
                        label="Salary"
                        value={`Rp ${employee.salary?.toLocaleString('id-ID') || employee.salary}`}
                        iconColor="text-yellow-600"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-3">
          <button
            onClick={() => onDelete(employee.id)}
            className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
          >
            <Trash2 className="w-4 h-4" />
            Delete Employee
          </button>
          
          <button
            onClick={() => onEdit(employee)}
            className="px-5 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
          >
            <Edit className="w-4 h-4" />
            Edit Employee
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeModal;